/*
 * /my-courses — chọn khoá để vào học. Trang MẪU của bản viết lại Simba.
 * Chưa có lớp nhưng là phụ huynh được uỷ quyền → chuyển sang /family.
 */
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { motion, useReducedMotion } from "framer-motion"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Icon } from "@/components/icon"
import { ErrorState, LoadingState, Page, PageHeader, usePageTitle } from "@/components/page"
import { CourseCodeForm } from "@/components/CourseCodeForm"
import { EditorialVisual } from "@/components/simba"
import { useSession } from "@/app/session"
import { api, loginUrl } from "@/lib/api"

export default function MyCoursesPage() {
  usePageTitle("Khóa học của tôi", "Không gian học tập cá nhân của học viên Simba.")
  const { loading, user } = useSession()
  const reduce = useReducedMotion()
  const [classes, setClasses] = useState<any[] | null>(null)
  const [courses, setCourses] = useState<any[]>([])
  const [error, setError] = useState("")

  const load = () => {
    setError("")
    Promise.all([api("/api/my/classes"), api("/api/public/courses"), api("/api/family").catch(() => ({ authorized: false }))])
      .then(([mine, catalog, family]) => {
        if (!mine.classes.length && family.authorized) return location.replace("/family")
        setClasses(mine.classes)
        setCourses(catalog.courses || [])
      })
      .catch((e: any) => {
        if (e.status === 401) location.href = loginUrl(location.href)
        else setError(e.message)
      })
  }
  useEffect(() => {
    if (loading) return
    if (!user) {
      location.href = loginUrl(location.href)
      return
    }
    load()
  }, [loading, user])

  if (loading || !user) return <LoadingState label="Đang mở không gian học tập…" />

  // `enrolled` = false: lớp mở mà học viên chưa tham gia — không được trông giống lớp của mình.
  const visible = [
    ...(classes ?? []).map((c) => ({ id: c.course_id, slug: c.course_slug, name: c.course_name, enrolled: c.enrolled !== false })),
    ...courses,
  ]
    .filter((c, i, all) => all.findIndex((x) => x.id === c.id) === i)
    .slice(0, 3)

  return (
    <Page>
      <div className="grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <div className="flex flex-col gap-6">
          <PageHeader
            eyebrow={`Chào ${String(user.full_name || "con").split(" ").slice(-1)[0]}!`}
            title={<>Chọn <em className="text-orange not-italic">khóa học</em></>}
            description="Vào lớp đang học, hoặc nhập mã lớp mentor gửi."
          />
          {error && <ErrorState message={error} onRetry={load} />}
          {!classes && !error && (
            <div className="flex flex-col gap-3">
              {[0, 1].map((i) => (
                <Skeleton key={i} className="h-20 rounded-[20px]" />
              ))}
            </div>
          )}
          {classes && (
            <motion.ul
              className="flex flex-col gap-3"
              initial={reduce ? false : "hidden"}
              animate="shown"
              variants={{ hidden: {}, shown: { transition: { staggerChildren: 0.05 } } }}
            >
              {visible.map((course: any) => {
                const joined = classes.find((c) => c.course_id === course.id)
                const to = joined ? `/classes/${encodeURIComponent(joined.slug)}` : `/courses/${course.slug}`
                return (
                  <motion.li key={course.id} variants={{ hidden: reduce ? {} : { y: 8 }, shown: { y: 0 } }}>
                    <Link to={to} className="group block">
                      <Card size="sm" className="transition-[transform,box-shadow] group-hover:-translate-y-0.5 group-hover:shadow-card-hover">
                        <CardContent className="flex items-center gap-4">
                          <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-soft">
                            <Icon name={joined ? "book-open" : "book"} className="size-6" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-heading text-lg font-semibold text-ink">{course.name}</span>
                            <span className="text-sm text-muted-foreground">{joined ? (course.enrolled === false ? "Lớp đang mở" : "Lớp của con") : "Xem khóa học"}</span>
                          </span>
                          {joined && course.enrolled === false && <Badge variant="warning">Lớp mở</Badge>}
                          <Icon name="arrow-right" className="size-5 transition-transform group-hover:translate-x-1" />
                        </CardContent>
                      </Card>
                    </Link>
                  </motion.li>
                )
              })}
            </motion.ul>
          )}
          <Card size="sm">
            <CardContent>
              <CourseCodeForm user={user} />
            </CardContent>
          </Card>
        </div>
        <EditorialVisual kind="savanna" alt="Sư tử con cùng ngựa vằn, lợn lòi và những người bạn giữa thảo nguyên" priority className="hidden lg:block" />
      </div>
    </Page>
  )
}
